import React, {type ReactNode} from 'react';
import type {Except} from 'type-fest';

import {useUniqueId} from '@packages/systems/spring-hooks';
import {useSize} from '@packages/utilities/styled';
import {FormLabel} from '../FormLabel';
import {Spacer} from '../Spacer';
import {StackItem, VStack} from '../Stack';

import type {StackProps} from '../Stack';
import type {HTMLAttributes, SpringTooling} from '../__SharedTypes';
import {
  FormGroupContext,
  type FormGroupProviderProps,
  type LabelWidth,
} from './context';
import {FormGroupView} from './FormGroup.styles';

export type FormGroupProps = {
  /**
   * The content for FormGroup.
   */
  children?: ReactNode;
  /**
   * Renders the error state for form elements within FormGroup.
   */
  isError?: boolean;
  /**
   * Renders the label and form elements horizontally.
   */
  isHorizontal?: boolean;
  /**
   * An accessible way to indicate that a form field is required
   */
  isRequired?: boolean;
  /**
   * The id for the FormGroup.
   */
  id?: string;
  /**
   * The label for the FormGroup.
   */
  label?: ReactNode;
  /**
   * The width of the label for the FormGroup.
   */
  labelWidth?: LabelWidth;
} & Except<StackProps, 'direction'> &
  HTMLAttributes &
  SpringTooling;

const FormGroupLabel = ({
  label,
  isHorizontal,
  isRequired,
}: {
  label?: ReactNode;
  isHorizontal?: boolean;
  isRequired?: boolean;
}) => {
  if (!label) return null;
  return (
    <StackItem>
      <FormLabel isHorizontal={isHorizontal} isRequired={isRequired}>
        {label}
      </FormLabel>
    </StackItem>
  );
};

/**
 * # FormGroup
 *
 * The `FormGroup` component is used to combine several form controls (e.g. `TextInput`) and form elements (e.g. `FormHelpText`) together.
 *
 * ## Usage
 *
 * ```jsx
 * import React from 'react';
 * import {
 *   FormGroup,
 *   useFormGroupId,
 *   useFormGroupState,
 * } from '@packages/systems/spring';
 *
 * const BaseTextInput = () => {
 *   const id = useFormGroupId();
 *   const {ariaDescribedBy} = useFormGroupState()
 *
 *   return <input type="text" id={id} aria-describedby={ariaDescribedBy} />;
 * };
 *
 * const BaseFormHelpText = props => {
 *   const {helpTextId} = useFormGroupState();
 *   return <span id={helpTextId} {...props} />;
 * };
 *
 * const Example = () => {
 *   return (
 *     <FormGroup label="What is your favourite season?">
 *       <BaseTextInput />
 *       <FormHelpText>All seasons are nice.</FormHelpText>
 *     </FormGroup>
 *   );
 * };
 * ```
 *
 * @component
 */
export const FormGroup = React.forwardRef<HTMLDivElement, FormGroupProps>(
  (
    {
      children,
      isError = false,
      isHorizontal = false,
      isRequired,
      id,
      label,
      labelWidth = 80,
      ...rest
    }: FormGroupProps,
    ref
  ) => {
    const size = useSize();
    const providerProps = {
      id,
      isError,
      isHorizontal,
      isRequired,
      labelWidth,
    } as const;

    const hasExtraSpace = size === 'x-large';

    return (
      <FormGroupProvider {...providerProps}>
        <FormGroupView
          align="start"
          direction={isHorizontal ? 'row' : 'column'}
          ref={ref}
          size={size}
          {...rest}
        >
          <FormGroupLabel
            isHorizontal={isHorizontal}
            isRequired={isRequired}
            label={label}
          />
          <Spacer>
            <VStack spacing={hasExtraSpace ? 2 : 1}>{children}</VStack>
          </Spacer>
        </FormGroupView>
      </FormGroupProvider>
    );
  }
);

FormGroup.displayName = 'FormGroup';
FormGroup.toString = FormGroupView.toString;

export default FormGroup;

export const FormGroupProvider = ({
  children,
  isError,
  isHorizontal,
  isRequired,
  id: preferredId,
  labelWidth,
}: FormGroupProviderProps) => {
  const baseId = useUniqueId(FormGroup, 'form-group', preferredId);
  /**
   * There may be a chance that multiple form controls render within FormGroup.
   * We need to ensure that the generated ids are bound only the first
   * rendered form control.
   */
  const [primaryId, setPrimaryId] = React.useState('');
  /**
   * Ensures that additional form controls aren't registered. Using ref instead
   * of state to prevent React from performing an unnecessary re-render.
   */
  const didSetControlId = React.useRef(false);

  /**
   * An internal method used by useFormGroupControl to register the control
   * with the this FormGroupState. It ensures that only the first control
   * is registered as the main id for the form group.
   *
   * This is a naive implementation, as it doesn't account for potential
   * input re-ordering (either due to sorting or unmounting).
   *
   * A more robust solution would be to leverage a Registration/Manager setup
   * - an implementation seen in the Descendant context pattern.
   *
   * For a future improvement to this implementation...
   * @see https://github.com/reach/reach-ui/tree/develop/packages/descendants
   */
  const registerControlId = React.useCallback(
    (id?: string, controlPreferredId?: string) => {
      if (didSetControlId.current) return;
      const automatedId = id ? `${baseId}-${id}` : baseId;
      const next = controlPreferredId || automatedId;
      setPrimaryId(next);
      didSetControlId.current = true;
    },
    [baseId]
  );

  /**
   * Generating the ideas for the various elements that may render within.
   */
  const controlId = primaryId || baseId;
  const htmlFor = controlId;
  const labelId = `${controlId}-label`;
  const helpTextId = `${controlId}-help-text`;
  const feedbackId = `${controlId}-feedback-text`;

  const ariaDescribedBy = isError ? feedbackId : helpTextId;

  const contextValue = {
    ariaDescribedBy,
    baseId,
    isError,
    labelId,
    feedbackId,
    helpTextId,
    htmlFor,
    isHorizontal,
    isRequired,
    labelWidth,
    // Used internally
    primaryId,
    registerControlId,
  } as const;

  return (
    <FormGroupContext.Provider value={contextValue}>
      {children}
    </FormGroupContext.Provider>
  );
};
